import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ConfidenceBreakdown } from "@/components/ConfidenceBreakdown";
import { EnrichButton } from "@/components/EnrichButton";
import {
  FeedbackButtons,
  type FeedbackState,
} from "@/components/FeedbackButtons";
import { Globe, Phone, Mail, MapPin } from "lucide-react";
import type { CompanyResult } from "@/types/api";
import { formatScrapedAt } from "@/lib/formatters";

// ── Types ────────────────────────────────────────────────────────────────────

type FreshnessLevel = "verified" | "scraped" | "inferred";
type FreshnessMap = Record<
  "website" | "phone" | "email" | "address",
  FreshnessLevel
>;
type ActiveTab = "bewertung" | "beschreibung" | "fakten";

// ── Props ────────────────────────────────────────────────────────────────────

interface CompanyDetailPanelProps {
  result: CompanyResult;
  jobId: string;
  expanded: boolean;
  feedbackState?: FeedbackState;
  onFeedback: (
    resultId: string,
    signal: "relevant" | "not_relevant",
    reason?: string,
    note?: string,
  ) => Promise<boolean>;
  onEnriched: (
    resultId: string,
    enrichmentData: Record<string, unknown>,
  ) => void;
}

// ── Pure helpers ─────────────────────────────────────────────────────────────

function websiteFreshness(
  sourceType: CompanyResult["source_type"],
): FreshnessLevel {
  switch (sourceType) {
    case "google_maps":
    case "local_pack":
      return "verified";
    case "google_search":
      return "scraped";
    default: {
      const _exhaustive: never = sourceType;
      void _exhaustive;
      return "scraped";
    }
  }
}

export function deriveFreshness(result: CompanyResult): FreshnessMap {
  const raw = result.enrichment_data ?? {};
  const hasEnrichment =
    typeof raw === "object" && raw !== null && Object.keys(raw).length > 0;
  // Access only the two fields we actually need; avoids unsafe full-interface cast
  const enrichRaw = raw as Record<string, unknown>;

  const contactFreshness = websiteFreshness(result.source_type);

  const phoneFreshness: FreshnessLevel = hasEnrichment
    ? enrichRaw.phone_verified === true
      ? "verified"
      : "scraped"
    : "inferred";

  const emailFreshness: FreshnessLevel = hasEnrichment
    ? enrichRaw.email_verified === true
      ? "verified"
      : "scraped"
    : "inferred";

  return {
    website: contactFreshness,
    address: contactFreshness,
    phone: phoneFreshness,
    email: emailFreshness,
  };
}

function normalizeUrl(url: string): string {
  return url.startsWith("http") ? url : `https://${url}`;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function FreshnessBadge({ level }: { level: FreshnessLevel }) {
  const { t } = useTranslation("results");
  const label =
    level === "verified"
      ? t("detail.freshVerified")
      : level === "scraped"
        ? t("detail.freshScraped")
        : t("detail.freshInferred");

  return (
    <span className="text-xs text-muted-foreground whitespace-nowrap">
      {label}
    </span>
  );
}

function ContactRow({
  icon: Icon,
  value,
  href,
  freshness,
}: {
  icon: React.ElementType;
  value: string | null | undefined;
  href?: string;
  freshness: FreshnessLevel;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1">
      <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline truncate block"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        ) : (
          <span className="text-sm truncate block">{value}</span>
        )}
      </div>
      <FreshnessBadge level={freshness} />
    </div>
  );
}

function FactTile({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="bg-muted/40 rounded-md p-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium truncate">{value}</p>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const TABS: ActiveTab[] = ["bewertung", "beschreibung", "fakten"];

export function CompanyDetailPanel({
  result,
  jobId,
  expanded,
  feedbackState,
  onFeedback,
  onEnriched,
}: CompanyDetailPanelProps) {
  const { t } = useTranslation("results");
  const [activeTab, setActiveTab] = useState<ActiveTab>("bewertung");

  // Reset tab whenever the displayed company changes
  useEffect(() => {
    setActiveTab("bewertung");
  }, [result.result_id]);
  // Also reset on collapse so the next open always starts at Bewertung
  useEffect(() => {
    if (!expanded) setActiveTab("bewertung");
  }, [expanded]);

  const freshness = deriveFreshness(result);
  const websiteUrl = result.website ? normalizeUrl(result.website) : null;

  const verdictKey =
    result.llm_relevance_score != null && result.llm_relevance_score >= 70
      ? "detail.verdictGood"
      : result.llm_relevance_score != null && result.llm_relevance_score >= 40
        ? "detail.verdictMid"
        : "detail.verdictLow";

  const sourceLabel =
    result.source_type === "google_search"
      ? t("detail.sourceGoogleSearch")
      : t("detail.sourceGoogleMaps");

  const handleTabKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    idx: number,
  ) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.stopPropagation();
      const next =
        e.key === "ArrowRight"
          ? (idx + 1) % TABS.length
          : (idx - 1 + TABS.length) % TABS.length;
      setActiveTab(TABS[next]);
      document.getElementById(`tab-${TABS[next]}`)?.focus();
    }
    // Do NOT stop Escape — InlineDetailExpansion's document listener must fire
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "bewertung":
        return <ConfidenceBreakdown result={result} />;
      case "beschreibung":
        return (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {result.description ?? t("detail.verdictNeutral")}
          </p>
        );
      case "fakten": {
        return (
          <div className="grid grid-cols-2 gap-2">
            <FactTile
              label={t("detail.factIndustry")}
              value={result.industry_llm}
            />
            <FactTile
              label={t("detail.factGoogleCat")}
              value={result.industry_google}
            />
            <FactTile label={t("detail.factCity")} value={result.city} />
            <FactTile
              label={t("detail.factSize")}
              value={result.employee_hint}
            />
            <FactTile label={t("detail.factSource")} value={sourceLabel} />
          </div>
        );
      }
      default: {
        const _exhaustive: never = activeTab;
        void _exhaustive;
        return null;
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* ── Hero verdict band ──────────────────────────────────────── */}
      <div
        data-testid="verdict-band"
        className="bg-primary/5 border border-primary/15 rounded-lg p-4 space-y-2"
      >
        <div className="flex items-start gap-2 justify-between">
          <div className="flex-1 min-w-0 space-y-1">
            <span className="text-xs font-semibold text-primary uppercase tracking-wide">
              {t(verdictKey)}
            </span>
            <p className="text-sm text-foreground leading-relaxed">
              {result.llm_relevance_reason ?? t("detail.verdictNeutral")}
            </p>
          </div>
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <FeedbackButtons
              resultId={result.result_id}
              feedbackState={feedbackState}
              onFeedback={onFeedback}
              expanded
              fullWidth={false}
            />
          </div>
        </div>

        {/* Keyword chips */}
        {result.matched_keywords.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {result.matched_keywords.map((kw) => (
              <span
                key={kw}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-success/10 text-success"
              >
                {kw}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Mid section: Kontakt | Tiefenrecherche ─────────────────── */}
      <div className="grid gap-4" style={{ gridTemplateColumns: "1.35fr 1fr" }}>
        {/* Left: contacts */}
        <div className="space-y-1">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide mb-2">
            {t("detail.sectionContact")}
          </h3>
          <ContactRow
            icon={Globe}
            value={result.website}
            href={websiteUrl ?? undefined}
            freshness={freshness.website}
          />
          <ContactRow
            icon={Phone}
            value={result.phone}
            href={result.phone ? `tel:${result.phone}` : undefined}
            freshness={freshness.phone}
          />
          <ContactRow
            icon={Mail}
            value={result.email}
            href={result.email ? `mailto:${result.email}` : undefined}
            freshness={freshness.email}
          />
          <ContactRow
            icon={MapPin}
            value={result.address}
            freshness={freshness.address}
          />
        </div>

        {/* Right: Tiefenrecherche */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide mb-2">
            {t("detail.sectionDeepResearch")}
          </h3>
          <EnrichButton
            result={result}
            jobId={jobId}
            onEnriched={onEnriched}
            fullWidth
          />
          <ul className="text-xs text-muted-foreground space-y-0.5 mt-2">
            <li>• {t("detail.enrichBulletExecutive")}</li>
            <li>• {t("detail.enrichBulletPhone")}</li>
            <li>• {t("detail.enrichBulletLinkedin")}</li>
          </ul>
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────── */}
      <div>
        <div role="tablist" className="flex border-b border-border">
          {TABS.map((tab, idx) => {
            const label =
              tab === "bewertung"
                ? t("detail.tabBewertung")
                : tab === "beschreibung"
                  ? t("detail.tabBeschreibung")
                  : t("detail.tabFakten");
            return (
              <button
                key={tab}
                role="tab"
                id={`tab-${tab}`}
                aria-selected={activeTab === tab}
                aria-controls={`panel-${tab}`}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === tab
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab(tab);
                }}
                onKeyDown={(e) => handleTabKeyDown(e, idx)}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Tab panels */}
        {TABS.map((tab) => (
          <div
            key={tab}
            role="tabpanel"
            id={`panel-${tab}`}
            aria-labelledby={`tab-${tab}`}
            hidden={activeTab !== tab}
            className="pt-4"
          >
            {activeTab === tab ? renderTabContent() : null}
          </div>
        ))}
      </div>

      {/* ── Provenance footer ──────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground/60 bg-muted/30 rounded-md px-3 py-2 flex-wrap">
        <span>{sourceLabel}</span>
        {result.scraped_at && (
          <>
            <span>·</span>
            <span>{formatScrapedAt(result.scraped_at)}</span>
          </>
        )}
        {result.industry_google && (
          <>
            <span>·</span>
            <span>{result.industry_google}</span>
          </>
        )}
        <span>·</span>
        <span>{result.result_id.slice(0, 8).toUpperCase()}</span>
      </div>
    </div>
  );
}
